// src/pages/Assets.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useLookups } from '../context/LookupContext';
import { useAppContext } from '../context/AppContext';
import { deleteassetsRecord, fetchassetsData, saveOrUpdateassetsRecord, fetchDependents } from '../services/assetService';
import { LookupValue } from '../services/lookupService';

// Import your new components
import Notification from '../components/shared/Notification';
import AssetHeader from '../components/assets/AssetHeader';
import AssetForm from '../components/assets/AssetForm';
import ActionBar from '../components/assets/ActionBar';
import AssetsTable from '../components/assets/AssetsTable';
import AssetsMobileView from '../components/assets/AssetsMobileView';
import Pagination from '../components/shared/Pagination';
import EmptyState from '../components/assets/EmptyState';
import { RefreshCw } from 'lucide-react';

export interface AssetData {
  id: string;
  assetOwnerType: string;
  assetType: string;
  assetSubType: string;
  assetName: string;
  assetPostCode: string;
  assetLoan: boolean;
  assetUnit: string;
  assetUnitPrice: string;
  assetCurrentPrice: string;
  assetspurchaseDate: string;
  assetspurchaseValue: string;
  status: string;
  familyMemberId?: string;
}

export type ColumnKey = keyof AssetData | 'ownerTypeLabel' | 'typeLabel' | 'subTypeLabel';

const visibleColumns: ColumnKey[] = [
  'assetOwnerType', 'assetName', 'assetType', 'assetSubType', 'assetCurrentPrice',
  'assetspurchaseDate', 'assetLoan', 'assetUnit', 'assetUnitPrice', 'assetPostCode', 'assetspurchaseValue'
];

const getColumnDisplayName = (col: ColumnKey): string => {
    const displayNames: Record<ColumnKey, string> = {
    id: 'ID', assetOwnerType: 'Owner Type', assetType: 'Type', assetSubType: 'Sub Type',
    assetName: 'Name', assetCurrentPrice: 'Current Price', assetspurchaseDate: 'Purchase Date',
    assetLoan: 'Loan', assetUnit: 'Unit', assetUnitPrice: 'Unit Price', assetPostCode: 'Post Code',
    assetspurchaseValue: 'Purchase Value', ownerTypeLabel: 'Owner Type', typeLabel: 'Type',
    subTypeLabel: 'Sub Type', status: 'Status', familyMemberId: '',
  };
  return displayNames[col];
};

const Assets: React.FC = () => {
    const { state } = useAppContext();
    const { getOptions, getLabel, isLoading: isLookupsLoading } = useLookups();

    const [transformedData, setTransformedData] = useState<AssetData[]>([]);
    const [dependents, setDependents] = useState<{ id: string; name: string }[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [apiLoading, setApiLoading] = useState(true);
    const [inlineEditingId, setInlineEditingId] = useState<string | null>(null);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    const [formData, setFormData] = useState<AssetData>({
        id: '', assetOwnerType: '', assetType: '', assetSubType: '', assetName: '', assetPostCode: '',
        assetLoan: false, assetUnit: '', assetUnitPrice: '', assetCurrentPrice: '', assetspurchaseDate: '',
        assetspurchaseValue: '', status: '', familyMemberId: ''
    });
    const [dropdownOptions, setDropdownOptions] = useState<{ ownerType: LookupValue[]; assetType: LookupValue[]; assetSubType: LookupValue[] }>({
        ownerType: [], assetType: [], assetSubType: []
    });
    const [loadingSubTypes, setLoadingSubTypes] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' | null }>({ key: 'assetType', direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ColumnKey, string>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    
    const lookupGroupMap: Record<string, string> = {
      assetOwnerType: 'ownerType', assetType: 'assetType', assetSubType: 'assetSubType',
    };

    const formatCurrency = useCallback((value: string | number) => {
        const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
        if (isNaN(num)) return 'N/A';
        const symbol = state.currencySymbol || '₹';
        return `${symbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.floor(num))}`;
    }, [state.currencySymbol]);

    const formatDate = (date: string) => {
        if (!date) return 'N/A';
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return 'N/A';
            const adjustedDate = new Date(d.valueOf() + d.getTimezoneOffset() * 60 * 1000);
            return `${String(adjustedDate.getDate()).padStart(2, '0')}/${String(adjustedDate.getMonth() + 1).padStart(2, '0')}/${adjustedDate.getFullYear()}`;
        } catch { return date; }
    };
    
    const resetForm = () => {
        setFormData({
            id: '', assetOwnerType: '', assetType: '', assetSubType: '', assetName: '', assetPostCode: '',
            assetLoan: false, assetUnit: '', assetUnitPrice: '', assetCurrentPrice: '', assetspurchaseDate: '',
            assetspurchaseValue: '', status: '', familyMemberId: ''
        });
        setEditingId(null);
    };

    const fetchAssetsFromAPI = useCallback(async () => {
        setApiLoading(true);
        try {
            const apiData = await fetchassetsData();
            if (!apiData || !Array.isArray(apiData.data)) throw new Error('Invalid API response structure');
            const rawAssets = apiData.data.map((item: any): AssetData => ({
                id: item.id || '', status: item.status || 'active', assetOwnerType: item.assetOwnerType || '',
                assetType: item.assetType || '', assetSubType: item.assetSubType || '', assetName: item.assetName || '',
                assetPostCode: item.assetPostCode || '', assetLoan: item.assetLoan === 'true' || item.assetLoan === true,
                assetUnit: item.assetUnit || '', assetUnitPrice: item.assetUnitPrice || '',
                assetCurrentPrice: item.assetCurrentPrice || '', assetspurchaseDate: item.assetspurchaseDate || '',
                assetspurchaseValue: item.assetspurchaseValue || '', familyMemberId: item.familyMemberId || '',
            }));
            setTransformedData(rawAssets);
        } catch (error) {
            console.error('Failed to fetch assets data:', error);
            setOperationStatus({ show: true, message: 'Failed to fetch assets.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, []);
    
     useEffect(() => { fetchAssetsFromAPI(); }, [fetchAssetsFromAPI]);
     useEffect(() => {
        if (!isLookupsLoading) {
            setDropdownOptions({
                ownerType: getOptions('ownerType'),
                assetType: getOptions('assetType'),
                assetSubType: [],
            });
        }
    }, [isLookupsLoading, getOptions]);

    useEffect(() => {
        const typeVal = formData.assetType;
        if (!typeVal) {
            setDropdownOptions(prev => ({ ...prev, assetSubType: [] }));
            return;
        }
        setLoadingSubTypes(true);
        const allSubTypes = getOptions('assetSubType');
        const filteredSubs = allSubTypes.filter(sub => sub.parentValue === typeVal);
        setDropdownOptions(prev => ({ ...prev, assetSubType: filteredSubs }));
        setLoadingSubTypes(false);
    }, [formData.assetType, getOptions]);

    useEffect(() => { fetchDependents().then(setDependents).catch(console.error); }, []);
    useEffect(() => {
        if (operationStatus.show) {
            const timer = setTimeout(() => setOperationStatus({ ...operationStatus, show: false }), 5000);
            return () => clearTimeout(timer);
        }
    }, [operationStatus]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'assetOwnerType' && { familyMemberId: '' }) }));
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.assetOwnerType || !formData.assetName || !formData.assetType) {
            return alert('Please fill in all required fields.');
        }
        if (formData.assetOwnerType === '2' && !formData.familyMemberId) {
            return alert('Please select a family member.');
        }
        setIsFormSubmitted(true);
        try {
            await saveOrUpdateassetsRecord(formData);
            setShowForm(false);
            resetForm();
            await fetchAssetsFromAPI();
            setOperationStatus({ show: true, message: `Asset ${editingId ? 'updated' : 'created'} successfully!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: `Error: ${error.message || 'Failed to save asset.'}`, type: 'error' });
        } finally {
            setIsFormSubmitted(false);
        }
    };

    const handleSort = (key: ColumnKey) => {
        let direction: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...transformedData].sort((a, b) => {
            const group = lookupGroupMap[key];
            let aValue: any = group ? getLabel(group, a[key as keyof AssetData] as string) : a[key as keyof AssetData];
            let bValue: any = group ? getLabel(group, b[key as keyof AssetData] as string) : b[key as keyof AssetData];

            if (['assetCurrentPrice', 'assetUnit', 'assetUnitPrice', 'assetspurchaseValue'].includes(key)) {
                aValue = parseFloat(aValue || '0');
                bValue = parseFloat(bValue || '0');
            } else if (key === 'assetspurchaseDate') {
                aValue = new Date(aValue).getTime() || 0;
                bValue = new Date(bValue).getTime() || 0;
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setTransformedData(sortedData);
    };

    const handleEdit = (asset: AssetData) => {
        setFormData({ ...asset });
        setEditingId(asset.id);
        setShowForm(true);
    };

   const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this asset record?')) {
      try {
        await deleteassetsRecord(id);
        setOperationStatus({ show: true, message: 'Asset record deleted successfully!', type: 'success' });
        setSelectedRows(prev => prev.filter(rowId => rowId !== id));
        await fetchAssetsFromAPI();
      } catch (error: any) {
        console.error('Delete failed:', error);
        setOperationStatus({ show: true, message: error.message || 'Failed to delete asset record.', type: 'error' });
      }
    }
  };
  
    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected assets?`)) {
            try {
                await Promise.all(selectedRows.map(id => deleteassetsRecord(id)));
                setTransformedData(prevData => prevData.filter(item => !selectedRows.includes(item.id)));
                setSelectedRows([]);
                setOperationStatus({ show: true, message: 'Selected assets deleted successfully!', type: 'success' });
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Bulk delete failed.', type: 'error' });
            }
        }
    };

    const handleRowSelect = (id: string) => setSelectedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
    
    const handleSelectAll = () => {
        if (selectedRows.length === paginatedData.length && paginatedData.length > 0) {
            setSelectedRows([]);
        } else {
            setSelectedRows(paginatedData.map(d => d.id));
        }
    };

    // const exportToCSV = () => {
    //     const headers = visibleColumns.map(col => getColumnDisplayName(col));
    //     const rows = filteredData.map(asset => visibleColumns.map(col => {
    //         const group = lookupGroupMap[col];
    //         if (group) return getLabel(group, asset[col as keyof AssetData] as string);
    //         if (['assetCurrentPrice', 'assetUnitPrice', 'assetspurchaseValue'].includes(col)) return formatCurrency(asset[col as keyof AssetData] as string);
    //         if (col === 'assetspurchaseDate') return formatDate(asset.assetspurchaseDate);
    //         if (col === 'assetLoan') return asset.assetLoan ? 'Yes' : 'No';
    //         return asset[col as keyof AssetData] || 'N/A';
    //     }));
    //     const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
    //     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    //     const link = document.createElement('a');
    //     link.href = URL.createObjectURL(blob);
    //     link.download = 'assets_data.csv';
    //     link.click();
    // };

    // const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const file = e.target.files?.[0];
    //     if (!file) return;
    //     try {
    //         const text = await file.text();
    //         const importedData = JSON.parse(text).assets as AssetData[];
    //         await Promise.all(importedData.map(asset => saveOrUpdateassetsRecord(asset)));
    //         setOperationStatus({ show: true, message: 'Data imported successfully!', type: 'success' });
    //         fetchAssetsFromAPI();
    //     } catch (error: any) {
    //         setOperationStatus({ show: true, message: error.message || 'Failed to import data.', type: 'error' });
    //     }
    // };
    
    const filteredData = transformedData.filter(asset => {
         const getDisplayValue = (colKey: keyof AssetData) => {
            const group = lookupGroupMap[colKey];
            return group ? getLabel(group, asset[colKey] as string) : asset[colKey] || 'N/A';
        };
        const searchMatch = searchTerm ? Object.keys(asset).some(key => 
            String(getDisplayValue(key as keyof AssetData)).toLowerCase().includes(searchTerm.toLowerCase())
        ) : true;
        const filterMatch = Object.entries(columnFilters).every(([key, value]) => 
            !value || String(getDisplayValue(key as keyof AssetData)).toLowerCase().includes(String(value).toLowerCase())
        );
        return searchMatch && filterMatch;
    });

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    const totalAssets = transformedData.reduce((sum, asset) => sum + parseFloat(asset.assetCurrentPrice || '0'), 0);
    const getSortIndicator = (key: ColumnKey) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '');
    const handleAddClick = () => { resetForm(); setShowForm(true); };
    const handleCancelForm = () => { resetForm(); setShowForm(false); };

    const paginationProps = {
        currentPage, totalPages, totalItems, itemsPerPage, startIndex, endIndex,
        handlePageChange: setCurrentPage,
        handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); },
    };

    return (
        <div className="space-y-8 p-4 sm:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />

            <AssetHeader
                totalAssets={totalAssets}
                recordCount={filteredData.length}
                apiLoading={apiLoading}
                onAdd={handleAddClick}
                formatCurrency={formatCurrency}
            />

            {showForm && (
                <AssetForm
                    formData={formData}
                    editingId={editingId}
                    isFormSubmitted={isFormSubmitted}
                    dropdownOptions={dropdownOptions}
                    dependents={dependents}
                    loadingSubTypes={loadingSubTypes}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    onCancel={handleCancelForm}
                />
            )}

            <AssetsMobileView
                paginatedData={paginatedData}
                apiLoading={apiLoading}
                searchTerm={searchTerm}
                onSearchChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getLabel={getLabel}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                {...paginationProps}
            />
            {filteredData.length >0 && (
              <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-x-auto">
                  <ActionBar
                      searchTerm={searchTerm}
                      onSearchChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      // onExport={exportToCSV}
                      // onImport={handleImportJSON}
                      onBulkDelete={handleBulkDelete}
                      selectedRowCount={selectedRows.length}
                  />
                  {apiLoading ? (
                      <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-green-600" /></div>
                  ) : (
                      <AssetsTable
                          paginatedData={paginatedData}
                          visibleColumns={visibleColumns}
                          getColumnDisplayName={getColumnDisplayName}
                          getSortIndicator={getSortIndicator}
                          handleSort={handleSort}
                          columnFilters={columnFilters}
                          setColumnFilters={setColumnFilters}
                          selectedRows={selectedRows}
                          handleSelectAll={handleSelectAll}
                          handleRowSelect={handleRowSelect}
                          handleEdit={handleEdit}
                          handleDelete={handleDelete}
                          formatCurrency={formatCurrency}
                          formatDate={formatDate}
                          getLabel={getLabel}
                          lookupGroupMap={lookupGroupMap}
                          inlineEditingId={inlineEditingId}
                          setInlineEditingId={setInlineEditingId}
                      />
                  )}
                  <Pagination {...paginationProps} />
              </div>
            )}
            
             {filteredData.length === 0 && !apiLoading && (
                <EmptyState onAdd={handleAddClick} />
            )}
        </div>
    );
};

export default Assets;